import { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Building2 } from "lucide-react";

const LANGS = ["Bahasa Indonesia", "English"];

export default function FirmSettings() {
  const [f, setF] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/firm/branding").then((r) => setF(r.data || {})).catch(() => setF({})); }, []);

  const save = async () => {
    try { await api.put("/firm/branding", f); toast.success("Pengaturan KAP disimpan"); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("logo_base64", reader.result);
    reader.readAsDataURL(file);
  };

  if (!f) return <Layout><PageHeader title="Pengaturan KAP" /><div className="p-6 text-sm text-muted-foreground">Memuat...</div></Layout>;

  return (
    <Layout>
      <PageHeader breadcrumb="Admin" title="Pengaturan KAP" subtitle="Identitas kantor & branding — dipakai pada seluruh ekspor Word/Excel"
        actions={<Button size="sm" onClick={save} data-testid="firm-save"><Save size={14} className="mr-1" />Simpan</Button>} />
      <div className="p-6 grid lg:grid-cols-3 gap-6" data-testid="firm-settings-page">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-border rounded-sm p-5 space-y-3">
            <div className="font-head font-semibold flex items-center gap-2"><Building2 size={16} />Identitas Kantor</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Nama Legal KAP</Label><Input value={f.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} data-testid="firm-legal" /></div>
              <div><Label>Nama Tampilan</Label><Input value={f.display_name || ""} onChange={(e) => set("display_name", e.target.value)} data-testid="firm-display" /></div>
              <div><Label>Nama Kantor / Cabang</Label><Input value={f.office_name || ""} onChange={(e) => set("office_name", e.target.value)} /></div>
              <div><Label>Nomor Izin (License)</Label><Input value={f.license_meta || ""} onChange={(e) => set("license_meta", e.target.value)} /></div>
            </div>
            <div><Label>Alamat</Label><Textarea rows={2} value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Telepon</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
              <div><Label>Email</Label><Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
              <div><Label>Website</Label><Input value={f.website || ""} onChange={(e) => set("website", e.target.value)} /></div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-5 space-y-3">
            <div className="font-head font-semibold">Bahasa & Footer</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Bahasa Utama</Label>
                <Select value={f.primary_language || "Bahasa Indonesia"} onValueChange={(v) => set("primary_language", v)}>
                  <SelectTrigger data-testid="firm-lang1"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Bahasa Sekunder</Label>
                <Select value={f.secondary_language || "English"} onValueChange={(v) => set("secondary_language", v)}>
                  <SelectTrigger data-testid="firm-lang2"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Footer Kerahasiaan</Label><Textarea rows={2} value={f.confidentiality_footer || ""} onChange={(e) => set("confidentiality_footer", e.target.value)} placeholder="mis. RAHASIA — Untuk penggunaan internal KAP" /></div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-sm p-5 space-y-3">
            <div className="font-head font-semibold">Logo KAP</div>
            <div className="border border-dashed border-border rounded-sm h-32 flex items-center justify-center bg-muted/30 overflow-hidden">
              {f.logo_base64 || f.logo_url
                ? <img src={f.logo_base64 || f.logo_url} alt="Logo KAP" className="max-h-28 object-contain" data-testid="firm-logo-preview" />
                : <span className="text-xs text-muted-foreground">Belum ada logo</span>}
            </div>
            <input type="file" accept="image/*" onChange={onLogo} className="text-xs" data-testid="firm-logo-input" />
            <div><Label className="text-xs">atau URL Logo</Label><Input value={f.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." /></div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
